/**
 * ONE aggregation of proposal lines — by store, by article, or by store × article — for every
 * place this chapter adds lines up (bata-ui issue 342 §3, PR B, `docs/adr/6020`).
 *
 * The proposal read publishes one line per store × article × size. Before this module the chapter
 * summed those lines in four places, each with its own loop: the run summary's shortfall
 * (`runShortfall`), the store page's articles (`storeArticles`), the department page's stores
 * (`departmentStores`), and now the Proposals page's store and article views with their side
 * panel. Four loops are four numbers at the first change. So the per-line accounting lives here,
 * once, and every one of them calls it:
 *
 *   - requested and proposed pairs: Σ `requested_units`, Σ `units`;
 *   - **uncovered**: Σ per line max(0, requested − units) — the shortfall of ADR 6019, never
 *     Σ requested − Σ units, which nets a floored line's extra pairs against another's missing;
 *   - the over-delivery apart: Σ per line max(0, units − requested), and the lines carrying it;
 *   - **the missing pairs by cause**: each short line through `lineShortfallByCause` — the engine's
 *     negative drivers, most negative first, up to what the line lacks, the rest «non attribuito».
 *     The same attribution «Non coperto», «In breve» and the run summary use (`./uncovered.ts`).
 *
 * A group's missing pairs by cause sum to its uncovered pairs, and the groups of any grain sum to
 * the run's: grouping only partitions the lines. The tests assert both.
 *
 * Pure: sums over the rows the page already holds; nothing is read for it.
 */

import type { ProposalSummary } from '@/api/generated';

import { isDroppedDoorLine, type DroppedDoorCandidate } from './dropped-door';
import { lineCoverage, uncoveredUnits } from './uncovered';

/**
 * The engine's three cuts, the transport cost's (`shipment_cost`, platform ADR 6188), and the
 * remainder no driver explains, in the order every call-out lists them.
 */
export const SHORTFALL_CAUSES = ['dc_availability', 'allocation', 'case_rounding', 'shipment_cost', 'unattributed'] as const;
export type ShortfallCause = (typeof SHORTFALL_CAUSES)[number];

/** Each cause in words — the same labels as the run summary's call-out. */
export const SHORTFALL_CAUSE_KEY: Readonly<Record<ShortfallCause, string>> = {
  dc_availability: 'inseason.shortfall.cause.dc_availability',
  allocation: 'inseason.shortfall.cause.allocation',
  case_rounding: 'inseason.shortfall.cause.case_rounding',
  shipment_cost: 'inseason.shortfall.cause.shipment_cost',
  unattributed: 'inseason.shortfall.cause.unattributed',
};

/** The fields a line contributes to a tally. */
export type TalliedLine = Pick<ProposalSummary, 'units' | 'requested_units' | 'drivers'> & Partial<Omit<DroppedDoorCandidate, 'units'>>;

/**
 * One short line's missing pairs over the engine's causes. The negative engine drivers, most
 * negative first (`lineCoverage`'s order), each take what they cut up to what is still missing;
 * whatever is left is «non attribuito». So the parts always sum to the line's shortfall.
 */
export function lineShortfallByCause(line: TalliedLine): Readonly<Record<ShortfallCause, number>> {
  const out: Record<ShortfallCause, number> = { dc_availability: 0, allocation: 0, case_rounding: 0, shipment_cost: 0, unattributed: 0 };
  const coverage = lineCoverage(line);
  if (coverage.kind === 'covered') return out;
  let left = coverage.units;
  if (coverage.kind === 'short') {
    for (const reason of coverage.reasons) {
      const cause = reason.code as ShortfallCause;
      if (!(SHORTFALL_CAUSES as readonly string[]).includes(cause) || left <= 0) continue;
      const taken = Math.min(left, -reason.contribution);
      out[cause] += taken;
      left -= taken;
    }
  }
  out.unattributed += left;
  return out;
}

/** What a set of lines adds up to. */
export interface ProposalTally {
  /** Lines summed. */
  readonly lines: number;
  /** Σ `requested_units` — pairs. */
  readonly requested: number;
  /** Σ `units` — pairs proposed. */
  readonly units: number;
  /** Σ max(0, requested − units) per line — pairs. */
  readonly uncovered: number;
  /** Σ max(0, units − requested) per line — apart, never netted. */
  readonly over: number;
  /** Lines proposed above their request. */
  readonly overLines: number;
  /** `uncovered` by engine cause, every cause listed. Sums to `uncovered`. */
  readonly byCause: Readonly<Record<ShortfallCause, number>>;
  /**
   * The part of `requested` that is a dropped door's need (`./dropped-door.ts`): pairs no size run could
   * carry, on lines that propose nothing. Counted where the lines are folded; a group the platform
   * summed (`GET …/proposals/groups`) does not state it, and omits it rather than say nought.
   */
  readonly dropped?: number;
  /** The group's lines that are dropped-door markers (bata-platform ADR 6183); the others are `lines − droppedLines`. */
  readonly droppedLines?: number;
}

interface Accumulator {
  lines: number;
  requested: number;
  units: number;
  uncovered: number;
  over: number;
  overLines: number;
  byCause: Record<ShortfallCause, number>;
  dropped: number;
  droppedLines: number;
}

const open = (): Accumulator => ({
  lines: 0, requested: 0, units: 0, uncovered: 0, over: 0, overLines: 0, dropped: 0, droppedLines: 0,
  byCause: { dc_availability: 0, allocation: 0, case_rounding: 0, shipment_cost: 0, unattributed: 0 },
});

/** The one place a line is added to a total. */
function add(into: Accumulator, line: TalliedLine): void {
  into.lines += 1;
  into.requested += line.requested_units;
  into.units += line.units;
  if (isDroppedDoorLine(line)) {
    into.dropped += Math.max(0, line.requested_units);
    into.droppedLines += 1;
  }
  const extra = Math.max(0, line.units - line.requested_units);
  if (extra > 0) { into.over += extra; into.overLines += 1; }
  const missing = uncoveredUnits(line);
  if (missing === 0) return;
  into.uncovered += missing;
  const parts = lineShortfallByCause(line);
  for (const cause of SHORTFALL_CAUSES) into.byCause[cause] += parts[cause];
}

/** Every line added up. Pure. */
export function tallyLines(lines: readonly TalliedLine[]): ProposalTally {
  const into = open();
  for (const line of lines) add(into, line);
  return into;
}

/** How the lines are grouped: one row per store, per article, or per store × article (the engine's grain). */
export type ProposalGrain = 'store' | 'article' | 'store_article';

/** One group of lines and what they add up to. */
export interface ProposalGroup extends ProposalTally {
  /** The store key, the article key, or `store|article` (`storeArticleRowId`, the «Perché» `?row=`). */
  readonly id: string;
  /** The store, unless the group is an article's across stores. */
  readonly storeKey: string | null;
  /** The article, unless the group is a store's across articles. */
  readonly articleKey: string | null;
  /** Distinct stores, articles and sizes (`size_key`) among the lines. */
  readonly stores: number;
  readonly articles: number;
  readonly sizes: number;
  /**
   * The group's distinct stores with at least one line that is not a marker, and those with nothing but
   * markers (ADR 6183). A store's status is relative to the group: dropped for one article, served for
   * another, it is served in its store's group and dropped in the first article's.
   */
  readonly servedDoors?: number;
  readonly droppedDoors?: number;
  /** The same for articles (bata-platform ADR 6185): with at least one line that is not a marker, and with nothing but markers. */
  readonly servedArticles?: number;
  readonly droppedArticles?: number;
}

type GroupedLine = TalliedLine & Pick<ProposalSummary, 'store_key' | 'article_key' | 'size_key'>;

/** The group a line belongs to at `grain`. `store|article` is `storeArticleRowId`'s spelling. */
export function groupId(line: Pick<ProposalSummary, 'store_key' | 'article_key'>, grain: ProposalGrain): string {
  if (grain === 'store') return line.store_key;
  if (grain === 'article') return line.article_key;
  // `storeArticleRowId`'s spelling, written here so this pure module does not pull in the
  // queue's dispositions through `./store-article-rows`; a test holds the two equal.
  return `${line.store_key}|${line.article_key}`;
}

/**
 * The lines grouped at `grain`, each group tallied — most pairs proposed first, ties by id (stable,
 * the order the store and department pages always drew). Pure.
 */
export function groupProposals(lines: readonly GroupedLine[], grain: ProposalGrain): readonly ProposalGroup[] {
  const groups = new Map<string, {
    sum: Accumulator; storeKey: string; articleKey: string;
    stores: Set<string>; articles: Set<string>; sizes: Set<string>; served: Set<string>; servedArticles: Set<string>;
  }>();
  for (const line of lines) {
    const id = groupId(line, grain);
    let entry = groups.get(id);
    if (entry === undefined) {
      entry = { sum: open(), storeKey: line.store_key, articleKey: line.article_key, stores: new Set(), articles: new Set(), sizes: new Set(), served: new Set(), servedArticles: new Set() };
      groups.set(id, entry);
    }
    add(entry.sum, line);
    entry.stores.add(line.store_key);
    entry.articles.add(line.article_key);
    entry.sizes.add(line.size_key);
    if (!isDroppedDoorLine(line)) {
      entry.served.add(line.store_key);
      entry.servedArticles.add(line.article_key);
    }
  }
  return [...groups.entries()]
    .map(([id, entry]): ProposalGroup => ({
      ...entry.sum,
      id,
      storeKey: grain === 'article' ? null : entry.storeKey,
      articleKey: grain === 'store' ? null : entry.articleKey,
      stores: entry.stores.size,
      articles: entry.articles.size,
      sizes: entry.sizes.size,
      servedDoors: entry.served.size,
      droppedDoors: entry.stores.size - entry.served.size,
      servedArticles: entry.servedArticles.size,
      droppedArticles: entry.articles.size - entry.servedArticles.size,
    }))
    .sort((a, b) => b.units - a.units || a.id.localeCompare(b.id));
}

/** The causes a tally lost pairs to, in `SHORTFALL_CAUSES` order, the empty ones left out. */
export function missingCauses(tally: Pick<ProposalTally, 'byCause'>): readonly { readonly cause: ShortfallCause; readonly pairs: number }[] {
  return SHORTFALL_CAUSES
    .map((cause) => ({ cause, pairs: tally.byCause[cause] }))
    .filter((entry) => entry.pairs > 0);
}
